import { useCallback, useEffect, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import { TToast } from 'types';

export const useTcoDashboardExports = (ref: React.RefObject<HTMLElement>) => {
  const [pdfTitle, setPdfTitle] = useState('');
  const [shouldPrint, setShouldPrint] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => ref.current,
    documentTitle: pdfTitle,
    pageStyle: `
        .footer-print-container {
          display: block !important;
          page-break-before:avoid;
          padding-bottom:20px
        }
        @page {
         size: A3;
         
        }
        .tco-line-graph,
        .bar-graph {
          display: flex;
          flex-direction:column;
          min-height:100vh;
        }
        
        .chart-container,.bar-graph-section {
          flex-grow:1;
          
        }
        .top-space{
          height:20px
        }

        @media print{
          .kite-card {
            border: none !important;
          }
          .footer-print-container {
            display: block !important;
          }
          @page { margin: 0 20px;}
        }
       `,
  });

  const handleSummaryDownloadImage = useCallback(
    async (id: string) => {
      const canvas = await html2canvas(ref.current!, {
        allowTaint: true,
        scale: 2,
        onclone: (clonedDoc) => {
          const clonedParentDiv = clonedDoc.getElementById(id);
          if (clonedParentDiv) {
            const legalDisclaimers = clonedParentDiv.querySelector(
              '.footer-print-container'
            ) as HTMLElement;
            if (legalDisclaimers) {
              legalDisclaimers.style.setProperty(
                'display',
                'block',
                'important'
              );
              legalDisclaimers.style.setProperty(
                'margin-top',
                '60px',
                'important'
              );
            }
          }
        },
      });

      const padding = 30;
      const paddedCanvas = document.createElement('canvas');
      paddedCanvas.width = canvas.width + padding * 2;
      paddedCanvas.height = canvas.height + padding * 2;
      paddedCanvas.style.display = 'initial';
      const ctx = paddedCanvas.getContext('2d');
      if (!ctx) return;
      // Fill the background with white color (optional)
      ctx.fillStyle = '#f8f8f8';
      ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);

      // Draw the original canvas onto the padded canvas
      ctx.drawImage(canvas, padding, padding);
      const dataURL = paddedCanvas.toDataURL('image/jpg');

      const link = document.createElement('a');
      link.href = dataURL;
      link.download = 'summary.jpg';
      link.click();
    },
    [ref]
  );

  const downloadChartAsImage = useCallback(
    (name: string, id: string) => {
      const chartRef = ref.current;

      if (chartRef) {
        html2canvas(chartRef, {
          scale: 2,
          ignoreElements: (element) => {
            return element.id === 'context-section';
          },
          onclone: (clonedDoc) => {
            const clonedDropdown = clonedDoc.getElementById(
              'dashboard-dropdown-term'
            ) as HTMLElement;
            if (clonedDropdown) {
              clonedDropdown.style.paddingTop = '8px';
            }
            const clonedParentDiv = clonedDoc.getElementById(id);
            if (clonedParentDiv) {
              const legalDisclaimers = clonedParentDiv.querySelector(
                '.footer-print-container'
              ) as HTMLElement;
              if (legalDisclaimers) {
                legalDisclaimers.style.setProperty(
                  'display',
                  'block',
                  'important'
                );
                legalDisclaimers.style.setProperty(
                  'margin-top',
                  '80px',
                  'important'
                );
              }
            }
          },
        }).then((canvas) => {
          const url = canvas.toDataURL('image/jpg');
          const link = document.createElement('a');
          link.href = url;
          link.download = `${name}.jpg`;
          link.click();
        });
      }
    },
    [ref]
  );

  const copyChartToClipBoard = useCallback(
    async (
      setToast: React.Dispatch<React.SetStateAction<TToast>>,
      id: string
    ) => {
      const chartRef = ref.current;

      if (chartRef) {
        try {
          const canvasImage = await html2canvas(chartRef, {
            scale: 2,
            ignoreElements: (element) => {
              return element.id === 'context-section';
            },
            onclone: (clonedDoc) => {
              const clonedDropdown = clonedDoc.getElementById(
                'dashboard-dropdown-term'
              ) as HTMLElement;
              if (clonedDropdown) {
                clonedDropdown.style.paddingTop = '8px';
              }
              const clonedParentDiv = clonedDoc.getElementById(id);
              if (clonedParentDiv) {
                clonedParentDiv.style.setProperty(
                  'border',
                  'none',
                  'important'
                );
                const legalDisclaimers = clonedParentDiv.querySelector(
                  '.footer-print-container'
                ) as HTMLElement;
                if (legalDisclaimers) {
                  legalDisclaimers.style.setProperty(
                    'display',
                    'block',
                    'important'
                  );
                  legalDisclaimers.style.setProperty(
                    'margin-top',
                    '80px',
                    'important'
                  );
                }
              }
            },
          });

          canvasImage.toBlob((blob) => {
            if (blob) {
              const item = new ClipboardItem({ 'image/png': blob });
              navigator.clipboard.write([item]);

              setToast({
                type: 'confirm',
                title: 'Success',
                text: 'Chart copied',
              });
            }
          });
        } catch (error) {
          console.error('Failed to copy image to clipboard:', error);
        }
      }
    },
    [ref]
  );

  const handleChartPdf = useCallback((title: string) => {
    setPdfTitle(title);
    setShouldPrint(true);
  }, []);

  useEffect(() => {
    if (shouldPrint) {
      setShouldPrint(false);
      handlePrint();
    }
  }, [pdfTitle, handlePrint, shouldPrint]);

  return {
    downloadChartAsImage,
    copyChartToClipBoard,
    handleSummaryDownloadImage,
    handleChartPdf,
  };
};
